import { memo, ReactNode, useCallback } from 'react';
import { Link as RouterLink } from 'react-router';
import classes from 'classnames';
import { EventCollectorProps } from 'hooks/context/EventCollector/types';
import LinkEvent from 'hooks/context/EventCollector/events/LinkEvent';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import styles from './Link.module.scss';

export type Props = {
  to?: string;
  children: ReactNode;
  disabled?: boolean;
  pending?: boolean;
  className?: string;
  external?: boolean;
  target?: '_blank' | '_self' | '_parent' | '_top';
  dataTest?: string;
  size?: 'l' | 'm' | 's';
  onClick?: () => void;
};

function Link({
  to = '',
  children,
  disabled = false,
  pending = false,
  className,
  external = false,
  target = '_self',
  dataTest,
  size = 'm',
  onClick,
  eventCollector,
}: Props & EventCollectorProps) {
  const { track } = useEventCollector();

  const handleClick = useCallback(() => {
    onClick?.();

    if (eventCollector?.dontTrack) {
      return;
    }

    track(
      new LinkEvent({
        content: children?.toString() ?? to ?? '',
        description: to ? `link to ${to}` : 'link',
      }),
    );
  }, [eventCollector?.dontTrack, children, onClick, to, track]);

  if (disabled || pending)
    return (
      <span
        data-test={dataTest}
        className={classes(
          styles.link,
          styles[size],
          styles.disabled,
          className,
        )}
      >
        {children}
      </span>
    );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={handleClick}
        data-test={dataTest}
        className={classes(styles.link, styles[size], className)}
      >
        {children}
      </button>
    );
  }

  if (external)
    return (
      <a
        onClick={onClick}
        data-test={dataTest}
        className={classes(styles.link, styles[size], className)}
        href={to}
        target={target}
      >
        {children}
      </a>
    );

  return (
    <RouterLink
      data-test={dataTest}
      className={classes(styles.link, styles[size], className)}
      to={to}
      onClick={handleClick}
    >
      {children}
    </RouterLink>
  );
}

Link.defaultProps = {
  to: '',
  disabled: false,
  pending: false,
  className: undefined,
  external: false,
  target: '_self',
  dataTest: undefined,
  size: 'm',
  onClick: undefined,
};

const LinkMemo = memo(Link);

export default LinkMemo;
